/* eslint-disable no-console */
import {InfoCircleOutlined} from '@ant-design/icons';
import {Form} from 'antd';
import {useWatch} from 'antd/es/form/Form';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import React, {useEffect, useState} from 'react';
import ReactQuill from 'react-quill';

import 'react-quill/dist/quill.snow.css';
import {
  isFieldDisabled,
  isFieldRequired
} from '../constants/commonInputFormFunction';
import {IField} from '../interface/inputType';

const WysiwygInput: React.FC<{
  field: IField;
}> = ({field}) => {
  const FormInstance = useFormInstance();
  const {getFieldValue, setFieldValue} = FormInstance;

  const [wysiwygCharCount, setWysiwygCharCount] = useState<number>(0);
  const [wysiwygContent, setWysiwygContent] = useState<string>('');
  const [wysiwygKey, setWysiwygKey] = useState<number>(0);
  const [lastWysiwygPlainText, setLastWysiwygPlainText] = useState<string>('');

  // Track previous disabled state (same pattern as QuestionnaireSwitch)
  const prevDisabledStateFieldName = `__prevDisabled_${field.fieldName}`;

  const dependentFieldName = field.disabledOn?.fieldName;
  const watchFieldName = dependentFieldName || field.fieldName;
  let dependentValue = useWatch(watchFieldName, FormInstance);
  dependentValue = field?.conditions ? undefined : dependentValue;

  useEffect(() => {
    const existingValue = getFieldValue(field.fieldName);
    if (typeof existingValue === 'string' && existingValue) {
      const initialText = existingValue.replace(/\n$/, '');
      setWysiwygCharCount(initialText.replace(/[ \n]/g, '').length);
      setWysiwygContent(initialText ? `<p>${initialText}</p>` : '');
      setLastWysiwygPlainText(initialText);
    }
  }, []);

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const isDisabled = field.disabled || isFieldDisabled(field, getFieldValue);

  // Clear internal state when field transitions from enabled to disabled (same pattern as QuestionnaireSwitch)
  useEffect(() => {
    if (field?.conditions) {
      return;
    }

    if (!field.disabledOn || !dependentFieldName) {
      if (!field.disabledOn) {
        const prevState = getFieldValue(prevDisabledStateFieldName);
        if (prevState !== null && prevState !== undefined) {
          setFieldValue(prevDisabledStateFieldName, undefined);
        }
      }

      return;
    }

    const isCurrentlyDisabled = isFieldDisabled(field, getFieldValue);
    const prevDisabledState = getFieldValue(prevDisabledStateFieldName);

    // Check if field transitioned from enabled to disabled
    if (prevDisabledState === false && isCurrentlyDisabled) {
      // Field was enabled before but is now disabled - clear its value and internal state
      const currentValue = getFieldValue(field.fieldName);
      if (
        currentValue !== undefined &&
        currentValue !== null &&
        currentValue !== ''
      ) {
        setFieldValue(field.fieldName, undefined);
        // Clear internal WYSIWYG state
        setWysiwygContent('');
        setWysiwygCharCount(0);
        setLastWysiwygPlainText('');
        setWysiwygKey((k) => k + 1); // Force ReactQuill to re-render with empty content
      }
    }

    // Update previous disabled state in form field
    setFieldValue(prevDisabledStateFieldName, isCurrentlyDisabled);
  }, [
    dependentValue,
    field.fieldName,
    field.disabledOn,
    dependentFieldName,
    prevDisabledStateFieldName,
    getFieldValue,
    setFieldValue
  ]);

  useEffect(() => {
    if (field?.conditions) {
      if (field?.disabled) {
        const currentValue = getFieldValue(field.fieldName);
        if (
          currentValue !== undefined &&
          currentValue !== null &&
          currentValue !== ''
        ) {
          setFieldValue(field.fieldName, undefined);
          // Clear internal WYSIWYG state
          setWysiwygContent('');
          setWysiwygCharCount(0);
          setLastWysiwygPlainText('');
          setWysiwygKey((k) => k + 1); // Force ReactQuill to re-render with empty content
        }
      }
    }
  }, [field?.disabled]);

  const isRequired =
    field?.isRequired ??
    (field?.requiredOn && !field?.conditions
      ? isFieldRequired(field, getFieldValue)
      : false);

  return (
    <Form.Item
      name={field.fieldName}
      label={field.fieldLabel}
      rules={
        isRequired
          ? [{required: true, message: `${field.fieldLabel} is required`}]
          : []
      }
      tooltip={
        field.tooltip
          ? {
              title: field.tooltip,
              icon: <InfoCircleOutlined />
            }
          : ''
      }
      labelCol={field?.colSpan ? {span: 24} : undefined}
    >
      <>
        <ReactQuill
          key={wysiwygKey}
          placeholder={field.fieldLabel}
          style={{width: '100%'}}
          preserveWhitespace={true}
          readOnly={isDisabled}
          value={wysiwygContent}
          onChange={(content, delta, source, editor) => {
            const rawText = editor.getText();
            const htmlText = editor.getHTML();
            //
            const text = rawText.replace(/\n$/, '');
            const nonSpaceLength = text.replace(/[ \n]/g, '').length;
            const limit = field?.textLimit;

            if (limit && nonSpaceLength > limit) {
              setWysiwygCharCount(limit);
              setFieldValue(field.fieldName, lastWysiwygPlainText);

              setWysiwygKey((k) => k + 1);

              return;
            }

            setWysiwygContent(content);
            setFieldValue(field.fieldName, htmlText);
            setLastWysiwygPlainText(text);
            setWysiwygCharCount(nonSpaceLength);
          }}
        />
        {field?.textLimit && (
          <div
            style={{
              textAlign: 'right',
              marginTop: '4px',
              color: 'rgba(0,0,0,0.45)'
            }}
          >
            {wysiwygCharCount}/{field.textLimit}
          </div>
        )}
      </>
    </Form.Item>
  );
};

export default WysiwygInput;
